import React, { useEffect, useMemo, useState } from "react";
import { Mafs, Coordinates, Plot, Point, Line, Theme, Text } from "mafs";
import { Card, Slider, Alert } from "antd";

/**
 * EquationIntersection
 * - Plots two linear functions from config.eq1/ex2 (GraphFunction)
 * - Allows interactive slope/intercept exploration
 * - Shows:
 *   - intersection (computed live)
 *   - if config.intersection provided, shows it as an "Expected" target point
 *
 * Config shape (Java):
 * EquationIntersectionConfig(eq1: GraphFunction, eq2: GraphFunction, intersection: PointDefinition?)
 */
export const EquationIntersection = ({ config = {} }) => {
    // --------- helpers ----------
    const normalizeExpr = (expr) => {
        let s = (expr ?? "").toString().trim();
        if (!s) return "";

        // Strip common prefixes
        s = s.replace(/^y\s*=/i, "");
        s = s.replace(/^f\s*\(\s*x\s*\)\s*=/i, "");

        // Remove whitespace and normalize x / operators
        s = s.replace(/\s+/g, "");
        s = s.replace(/X/g, "x");
        s = s.replace(/\*/g, ""); // allow "2*x+1" -> "2x+1"
        if (s.startsWith("+")) s = s.slice(1);

        return s;
    };

    const safeNumber = (n, fallback = 0) => {
        const v = Number(n);
        return Number.isFinite(v) ? v : fallback;
    };

    const parseLinear = (rawExpr) => {
        const clean = normalizeExpr(rawExpr);
        if (!clean) return { m: 1, c: 0, expr: "x" };

        // Constant-only: "4", "-3.5"
        if (!clean.includes("x")) {
            const c0 = safeNumber(clean, 0);
            return { m: 0, c: c0, expr: clean };
        }

        // Split at first 'x': "<coef>x<const>"
        const idx = clean.indexOf("x");
        const coefStr = clean.slice(0, idx);     // "", "-", "2", "0.5", "-3.2"
        const restStr = clean.slice(idx + 1);    // "", "+1", "-4.5"

        let m = 1;
        if (coefStr === "" || coefStr === "+") m = 1;
        else if (coefStr === "-") m = -1;
        else m = safeNumber(coefStr, 1);

        let c = 0;
        if (restStr) c = safeNumber(restStr, 0);

        return { m, c, expr: clean };
    };

    const formatLinear = (m, c) => {
        const mm = Number.isFinite(m) ? m : 0;
        const cc = Number.isFinite(c) ? c : 0;
        const sign = cc >= 0 ? "+" : "-";
        return `y = ${mm}x ${sign} ${Math.abs(cc)}`;
    };

    const resolveColor = (maybeColor, fallback) => {
        if (typeof maybeColor !== "string") return fallback;
        const s = maybeColor.trim();
        return s ? s : fallback;
    };

    const resolveStyle = (maybeStyle) => {
        const s = (maybeStyle ?? "").toString().trim().toLowerCase();
        return s === "dashed" ? "dashed" : undefined;
    };

    // --------- config extraction ----------
    const eq1Cfg = config.eq1 || {};
    const eq2Cfg = config.eq2 || {};

    const init1 = useMemo(() => parseLinear(eq1Cfg.expression ?? "2x+1"), [eq1Cfg.expression]);
    const init2 = useMemo(() => parseLinear(eq2Cfg.expression ?? "-x+4"), [eq2Cfg.expression]);

    // Keep state in sync if config changes (important in widget pipelines)
    const [l1, setL1] = useState({ m: init1.m, c: init1.c });
    const [l2, setL2] = useState({ m: init2.m, c: init2.c });

    useEffect(() => setL1({ m: init1.m, c: init1.c }), [init1.m, init1.c]);
    useEffect(() => setL2({ m: init2.m, c: init2.c }), [init2.m, init2.c]);

    // Slider bounds: widen if config starts outside [-5,5]
    const sliderBounds = useMemo(() => {
        const maxAbs = Math.max(
            5,
            Math.abs(l1.m),
            Math.abs(l1.c),
            Math.abs(l2.m),
            Math.abs(l2.c)
        );
        const bound = Math.min(20, Math.ceil(maxAbs)); // cap to keep UI sane
        return { min: -bound, max: bound };
    }, [l1, l2]);

    const line1Color = resolveColor(eq1Cfg.color, Theme.blue);
    const line2Color = resolveColor(eq2Cfg.color, Theme.red);

    const line1Label = (eq1Cfg.label && String(eq1Cfg.label).trim()) || formatLinear(l1.m, l1.c);
    const line2Label = (eq2Cfg.label && String(eq2Cfg.label).trim()) || formatLinear(l2.m, l2.c);

    const line1Style = resolveStyle(eq1Cfg.style);
    const line2Style = resolveStyle(eq2Cfg.style);

    const expected = config.intersection || null;

    // --------- math ----------
    const relation = useMemo(() => {
        const eps = 1e-6;
        const dm = l1.m - l2.m;
        const dc = l1.c - l2.c;

        if (Math.abs(dm) < eps) {
            if (Math.abs(dc) < eps) return { kind: "COINCIDENT" };
            return { kind: "PARALLEL" };
        }

        const x = (l2.c - l1.c) / (l1.m - l2.m);
        const y = l1.m * x + l1.c;

        if (!Number.isFinite(x) || !Number.isFinite(y)) return { kind: "PARALLEL" };
        return { kind: "INTERSECT", x, y };
    }, [l1, l2]);

    // --------- render ----------
    return (
        <div className="flexColumn gap-medium width-100">
            {/* GRAPH AREA */}
            <div className="ch-emph-block bg-white" style={{ height: 400 }}>
                <Mafs viewBox={{ x: [-5, 5], y: [-5, 5] }} pan={false}>
                    <Coordinates.Cartesian />

                    {/* Line 1 */}
                    <Plot.OfX
                        y={(x) => l1.m * x + l1.c}
                        color={line1Color}
                        weight={3}
                        label={line1Label}
                        style={line1Style}
                    />

                    {/* Line 2 */}
                    <Plot.OfX
                        y={(x) => l2.m * x + l2.c}
                        color={line2Color}
                        weight={3}
                        label={line2Label}
                        style={line2Style}
                    />

                    {/* Expected (from backend config) */}
                    {expected && Number.isFinite(expected.x) && Number.isFinite(expected.y) && (
                        <>
                            <Point x={expected.x} y={expected.y} color={Theme.foreground} />
                            <Text x={expected.x} y={expected.y - 0.6} color={Theme.foreground} size={12}>
                                {expected.label ? String(expected.label) : "Expected"}
                            </Text>
                        </>
                    )}

                    {/* Intersection (live) */}
                    {relation.kind === "INTERSECT" && (
                        <>
                            <Line.Segment
                                point1={[relation.x, 0]}
                                point2={[relation.x, relation.y]}
                                style="dashed"
                                color={Theme.indigo}
                                opacity={0.5}
                            />
                            <Point x={relation.x} y={relation.y} color={Theme.indigo} />
                            <Text x={relation.x} y={relation.y + 0.5} color={Theme.indigo} weight="bold">
                                ({relation.x.toFixed(2)}, {relation.y.toFixed(2)})
                            </Text>
                        </>
                    )}

                    {relation.kind === "PARALLEL" && (
                        <Text x={0} y={1} color={Theme.red} size={18} weight="bold">
                            Parallel Lines (No Solution)
                        </Text>
                    )}

                    {relation.kind === "COINCIDENT" && (
                        <Text x={0} y={1} color={Theme.green} size={18} weight="bold">
                            Same Line (Infinitely Many Solutions)
                        </Text>
                    )}
                </Mafs>
            </div>

            {/* CONTROLS & MATH */}
            <div className="flexRow gap-medium align-start">
                {/* Sliders Card */}
                <Card className="flex1 ch-card bg-slate-50 border-all--s" title="Modify Equations" size="small">
                    <div className="flexColumn gap-small">
                        {/* Eq 1 Controls */}
                        <div className="padding-b--s border-b--s">
                            <span className="ch-text-bold" style={{ color: line1Color }}>Line 1</span>
                            <div className="flexAlignCentre gap-small margin-t--xs">
                                <span className="text-muted text-xs w-12">Slope (m)</span>
                                <Slider
                                    className="flex1"
                                    min={sliderBounds.min}
                                    max={sliderBounds.max}
                                    step={0.5}
                                    value={l1.m}
                                    onChange={(v) => setL1((p) => ({ ...p, m: v }))}
                                />
                                <span className="text-xs font-mono w-10 text-right">{l1.m}</span>
                            </div>
                            <div className="flexAlignCentre gap-small">
                                <span className="text-muted text-xs w-12">Y-Int (c)</span>
                                <Slider
                                    className="flex1"
                                    min={sliderBounds.min}
                                    max={sliderBounds.max}
                                    step={0.5}
                                    value={l1.c}
                                    onChange={(v) => setL1((p) => ({ ...p, c: v }))}
                                />
                                <span className="text-xs font-mono w-10 text-right">{l1.c}</span>
                            </div>
                        </div>

                        {/* Eq 2 Controls */}
                        <div className="padding-t--s">
                            <span className="ch-text-bold" style={{ color: line2Color }}>Line 2</span>
                            <div className="flexAlignCentre gap-small margin-t--xs">
                                <span className="text-muted text-xs w-12">Slope (m)</span>
                                <Slider
                                    className="flex1"
                                    min={sliderBounds.min}
                                    max={sliderBounds.max}
                                    step={0.5}
                                    value={l2.m}
                                    onChange={(v) => setL2((p) => ({ ...p, m: v }))}
                                />
                                <span className="text-xs font-mono w-10 text-right">{l2.m}</span>
                            </div>
                            <div className="flexAlignCentre gap-small">
                                <span className="text-muted text-xs w-12">Y-Int (c)</span>
                                <Slider
                                    className="flex1"
                                    min={sliderBounds.min}
                                    max={sliderBounds.max}
                                    step={0.5}
                                    value={l2.c}
                                    onChange={(v) => setL2((p) => ({ ...p, c: v }))}
                                />
                                <span className="text-xs font-mono w-10 text-right">{l2.c}</span>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Algebraic Solution Card */}
                <Card className="flex1 ch-card bg-white border-all--s" title="Algebraic Solution">
                    {relation.kind === "INTERSECT" ? (
                        <div className="flexColumn gap-small font-mono text-sm">
                            <div className="p-2 bg-slate-50 rounded">
                                <div className="text-muted text-xs mb-1">1. Equate both y values:</div>
                                <div>
                                    <span style={{ color: line1Color }}>{l1.m}x + {l1.c}</span>{" "}
                                    ={" "}
                                    <span style={{ color: line2Color }}>{l2.m}x + {l2.c}</span>
                                </div>
                            </div>

                            <div className="p-2 bg-slate-50 rounded">
                                <div className="text-muted text-xs mb-1">2. Group x terms:</div>
                                <div>
                                    {l1.m}x - {l2.m}x = {l2.c} - {l1.c}
                                </div>
                                <div>
                                    ({(l1.m - l2.m).toFixed(2)})x = {(l2.c - l1.c).toFixed(2)}
                                </div>
                            </div>

                            <div className="p-2 bg-green-50 border border-green-100 rounded">
                                <div className="text-green-800 text-xs mb-1 font-bold">3. Solve:</div>
                                <div className="font-bold">x = {relation.x.toFixed(2)}</div>
                                <div className="font-bold">y = {relation.y.toFixed(2)}</div>
                            </div>
                        </div>
                    ) : relation.kind === "COINCIDENT" ? (
                        <Alert
                            message="Infinitely Many Solutions"
                            description="The two equations represent the same line."
                            type="info"
                            showIcon
                        />
                    ) : (
                        <Alert
                            message="No Intersection"
                            description="The lines are parallel (same slope, different intercept), so they will never meet."
                            type="warning"
                            showIcon
                        />
                    )}
                </Card>
            </div>
        </div>
    );
};
